package dev.lingvo.shared

import kotlinx.serialization.Serializable

/** How loudly an attention item asks. Maps to the palette's danger / warning / info. */
@Serializable
enum class AttentionSeverity { DANGER, WARNING, INFO }

/**
 * What the card's one verb actually does.
 *
 * [OPEN] is the original behaviour and stays the default: follow [AttentionItemDto.actionHref].
 * [RETRY_PRETRANSLATE] is the one case where sending somebody to a page was the wrong answer — "Try
 * again" opened an empty pre-translate form and left them to reconstruct which provider and which
 * languages the failed run used, from a card that already knew both. The fields beside it carry
 * that intent so the card can act in place, with no second request.
 */
@Serializable
enum class AttentionKind { RETRY_PRETRANSLATE, OPEN }

/**
 * One thing somebody should do, in one sentence with one verb.
 *
 * **The binding rule, enforced in the endpoint rather than by good intentions: an item may exist
 * only if it is resolvable by the viewer's role and has a resolution condition that is not "you read
 * it."** Anything else is a report row and belongs in the readiness table below. Staleness is
 * deliberately excluded — a badge, never a nag.
 *
 * Without that rule the list becomes wallpaper within a month, and a permanent red thing that means
 * nothing is worse than the page it replaced.
 */
@Serializable
data class AttentionItemDto(
    /**
     * Stable identity, e.g. `credits_low` or `mt_job_failed:<uuid>`.
     *
     * Dismissals are recorded against it, so it must survive a redeploy and must not contain a
     * timestamp — an item whose key changed every hour could never be dismissed.
     */
    val key: String,
    val severity: AttentionSeverity,
    /** One sentence. What is true, and what happens if nobody acts. */
    val message: String,
    /** One verb: "Update payment method", "Retry", "Review". */
    val actionLabel: String,
    val actionHref: String,
    /** Whether this person may put it away for a week. */
    val dismissible: Boolean = true,
    /**
     * What the verb does. Defaulted to [AttentionKind.OPEN], so an old client that ignores every
     * field below still follows [actionHref] and nothing regresses.
     */
    val kind: AttentionKind = AttentionKind.OPEN,
    /** The project this is about, where there is one. */
    val projectSlug: String? = null,
    /** The language this is about, where there is one. */
    val locale: String? = null,
    /** The run that failed, for [AttentionKind.RETRY_PRETRANSLATE]. */
    val jobId: String? = null,
    /** The provider that run used, so retrying it does not guess. */
    val provider: MtProvider? = null,
    /** The languages that run covered, for the same reason. */
    val locales: List<String> = emptyList(),
)

/** One project's distance from shippable, for the overview's readiness table. */
@Serializable
data class OverviewProjectDto(
    val slug: String,
    val name: String,
    /** Worst locale first. Capped, because a list nobody scrolls is a list nobody reads. */
    val locales: List<LocaleReadinessDto> = emptyList(),
    val href: String,
) {
    val shippable: Boolean get() = locales.isNotEmpty() && locales.all { it.shippable }
}

/**
 * Where the workspace's credits stand, for the people who pay.
 *
 * ADMIN and above only. A translator has no business seeing what the workspace spends, and the API
 * refuses them the billing routes anyway.
 */
@Serializable
data class CreditPositionDto(
    val used: Int,
    val included: Int,
    /** Days left in the UTC billing period — the period `credit_usage` actually counts. */
    val daysRemaining: Int,
    /**
     * When the credits are projected to run out, or null for "won't run out this month".
     *
     * An ISO date rather than a sentence, so the client can say it in the reader's own format.
     */
    val exhaustionDate: String? = null,
)

/** One thing that happened, as a sentence with a real name in it. */
@Serializable
data class WorkLogEntryDto(
    val sentence: String,
    val href: String,
    val at: String,
    val workspaceSlug: String? = null,
    val projectSlug: String? = null,
    /**
     * The stored activity type — `FILE_IMPORTED`, `TRANSLATION_REVIEWED`, `MT_JOB_CANCELLED`.
     *
     * The wire constant, never a sentence: [sentence] is what a reader reads, and this is what the
     * client draws the row's mark from. Without it every row in the log carried the same generic
     * history glyph, so a column of twenty sentences had twenty identical marks and the eye had to
     * read all of them to find the import.
     *
     * Defaulted to null so an older server's response still deserializes and simply falls back to
     * that generic mark, which is what every client did before this field existed.
     */
    val type: String? = null,
    /**
     * Who did it, for the row's avatar and its bolded lead word.
     *
     * Null for a system action or a deleted user, same as [WorkLogEntryDto] falls back to a generic
     * mark when [type] is null. [sentence] already spells this out in English, so an old client that
     * ignores this field loses nothing; a client drawing its own per-kind phrasing needs the name on
     * its own, not embedded at the front of a sentence it cannot safely parse in every locale.
     */
    val actor: String? = null,
    /**
     * The locale the event happened in, where it has one — `TRANSLATION_REVIEWED` and
     * `TRANSLATION_UPDATED` always do, most other kinds don't.
     */
    val locale: String? = null,
    /**
     * `TRANSLATION_REVIEWED`'s payload `action` — `APPROVE` or `REQUEST_CHANGES` — the only field
     * that decides which of two opposite sentences a review event gets.
     */
    val action: String? = null,
)

/**
 * Everything `/app` needs, in one call.
 *
 * Today the page calls `listProjects()` and then fans out one `listProjectActivity()` per project
 * **from the browser**. Twelve projects on a scale-to-zero machine with a cold database connection
 * is a six-second first paint on the page that has to be fastest, and it degrades exactly as a
 * workspace grows.
 *
 * Every block is independently nullable, because **partial failure is the important case**: if the
 * credit block fails and readiness succeeded, the page renders readiness and puts a retry in that
 * block alone. A billing timeout must never blank the overview.
 */
@Serializable
data class OverviewResponse(
    /** The outstanding fact: "Two projects are release-ready. French is blocked on 12 QA errors." */
    val verdict: String,
    val attention: List<AttentionItemDto> = emptyList(),
    val projects: List<OverviewProjectDto> = emptyList(),
    val credits: CreditPositionDto? = null,
    val workLog: List<WorkLogEntryDto> = emptyList(),
    /**
     * Which blocks could not be computed, by id: `attention`, `readiness`, `credits`, `worklog`,
     * `providers`.
     */
    val failed: List<String> = emptyList(),
    /**
     * Whether this person is in a workspace at all.
     *
     * Distinct from "their workspace is empty", and the distinction is load-bearing: both arrive
     * with no projects and no work log, so [isFirstRun] could not tell them apart and offered
     * somebody with no workspace a button that seeds a sample project *into* one. It failed when
     * pressed.
     *
     * A field rather than a match on [verdict], because a verdict is a sentence written for a
     * reader and rewording it must not change what the client does.
     */
    val hasWorkspace: Boolean = true,
    /** The workspace this response resolved, or null for legacy/no-workspace responses. */
    val workspaceSlug: String? = null,
    /**
     * Whether any provider in this workspace is [ProviderReadiness.READY].
     *
     * Here rather than behind its own call because the shell needs it on every page to decide
     * whether to offer machine translation at all, and a banner that costs an extra request per
     * navigation is a banner somebody removes. False on a legacy response, which is the safe
     * direction: the worst it does is show a "set up a provider" prompt to a workspace that has one.
     */
    val providerReady: Boolean = false,
) {
    /**
     * True first-run: a workspace that is theirs, and nothing in it yet.
     *
     * [hasWorkspace] is part of the test on purpose — see its own note.
     */
    val isFirstRun: Boolean get() = hasWorkspace && projects.isEmpty() && workLog.isEmpty()
}

/** Put one attention item away for a week. */
@Serializable
data class DismissAttentionRequest(val key: String)
